import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { fabric } from 'fabric';
import { isMobile } from 'react-device-detect';
import { ConfirmationDialog, IconNames, ToastVariants, CUIColors, CUIText } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../context/analytics-context';
import {
  setAllDrawersClosed,
  setIsImageUploadDrawerOpen,
  setIsRotationDrawerOpen,
  setIsSystemErrorOpen,
  setIsToasterOpen,
  useAppContext,
} from '../../context/app-context';
import { useUIStore } from '../../stores/ui';
import { setSelectedPhotoZone, useCardContext } from '../../context/card-context';
import { useCardFaceReadinessStore } from '../../stores/card-face-readiness';
import { useInitializationDataContext } from '../../context/data-context';
import {
  OverlayTextarea,
  OverlayTextHandles,
} from '../../features/card-editor/components/overlay-textbox/overlay-textarea';
import { usePhotoControls } from '../../features/card-editor/hooks/use-photo-controls';
import { usePhotoTextZone } from '../../features/card-editor/hooks/use-photo-text-zone';
import { useTextArea } from '../../features/card-editor/hooks/use-text-area';
import {
  CanvasObjectReviver,
  captureCanvasSnapshot,
  EditorPresentation,
  enqueueCanvasOperation,
  getHistoryEditorPresentation,
  loadCanvasSnapshot,
  useEditHistoryCoordinator,
  useHistoryTransaction,
  withPreservedCanvasFrame,
} from '../../features/edit-history/edit-history';
import { getPhotoZoneButton } from '../card-editor/card-editor-utils';
import { manageFabricTextSize } from '../../features/card-editor/utils/manage-fabric-text-size';
import { CustomFabricImage, FabricObject, FabricTextBox } from '../../global-types/canvas';
import { CardType } from '../../global-types/card';
import {
  useActiveCanvas,
  useDimensionsWithBleed,
  useElementSize,
  useFeatureFlags,
  useIsSignAndSend,
} from '../../hooks';
import { ActionType, TextAction, useDatadog } from '../../hooks/use-datadog';
import { useFabric } from '../../hooks/use-fabric';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { useAddLowResIcon } from '../../hooks/use-add-low-res-icon';
import { useLowResIconTooltip } from '../../hooks/use-low-res-icon-tooltip';
import { LowResolutionTooltipTrigger } from '../low-resolution-tooltip/low-resolution-tooltip';
import { useCanvasAccessibility } from './hooks/use-canvas-accessibility';
import { useCanvasMouseUp } from './hooks/use-canvas-mouse-up';
import { useCanvasScalingFactor } from './hooks/use-canvas-scaling-factor';
import {
  addUserTextHandlers,
  CanvasDataTypes,
  getGroupedItemByName,
  getObjectByName,
  getPhotoTextZoneDescription,
  getZoneLabelOffset,
  hideMiddleControls,
  isEditableText,
  lockEditableSize,
  onPhotoTrayImageDrop,
  spaceContentEvenly,
} from '../../utils';
import { parseTextAction } from '../../utils/parse-text-action';
import { ensureHiddenTextareaMinFont } from '../../utils/ensure-hidden-textarea-min-font';
import { highlightSelectedPhotoZone } from '../../utils/canvas-utils/highlight-photo-zone';
import {
  clearPhotoZoneDragHighlight,
  handlePhotoZoneDragOver,
} from '../../utils/canvas-utils/handle-photo-zone-drag-over';
import { useOpenImageEditDrawer } from '../../features/card-editor/hooks/use-open-image-edit-drawer';
import { usePhoto } from '../../features/card-editor/hooks/use-photo';
import { findTextById } from '../../utils/utility/find-text-by-id';
import { isSplitPanelFace, setSplitPanelViewport } from '../../utils/panel-zoom';
import { loadPhotozoneObjects } from './utils/load-photozone-objects';
import { calculateAdjustedScale, closeResetTextModal, confirmResetText, setCroppedSrc } from './utils';
import { getAvailableCanvasHeight } from './utils/calculate-available-canvas-height';
import { addEditableAreas, addIconsToLowResImages, setCardRenderedWidth } from '../card-editor/utils';
import { FabricCanvas } from '../fabric-canvas/fabric-canvas';
import { isInsideImageEditToolbar } from '../card-controls/image-edit-toolbar';
import { isInsidePhotoDrawer } from '../card-controls/photo-drawer/photo-drawer-host';
import { isInsideWamInstructionsMenu } from '../card-controls/wam-instructions-menu/wam-instructions-menu-host';
import { isFloatingToolMenuInteraction } from '../floating-tool-menu/floating-tool-menu-host';
import { CardFaceProps } from './card-face-types';
import styles from './card-face.module.scss';
import colors from '../../styles/util.scss';

export const CardFace = ({
  cardFace,
  parentRef,
  navigationRef,
  useOverrideRef,
  addClass,
  cardIndex,
  editorSessionKey,
  prepareFace,
}: CardFaceProps): React.ReactElement => {
  const classes = classNames(styles['card-face'], addClass);

  // --- Context ---
  const { cardState, cardDispatch } = useCardContext();
  const setFaceReadiness = useCardFaceReadinessStore((state) => state.setFaceReadiness);
  const historyCoordinator = useEditHistoryCoordinator();
  const { appState, appDispatch } = useAppContext();
  const { firstElementSelected, updateEditFormats, trackWamTamReset, trackInitializeEdit, isReplacingImage } =
    useAnalyticsContext();
  const { initializedDataState } = useInitializationDataContext();
  const { t } = useTranslation();

  // --- Stores ---
  const { openTextDrawer, closeAllDrawers, closeImageEditToolbar, closeFontToolbar } = useUIStore((state) => state);

  // --- Hooks ---
  const activeCanvas = useActiveCanvas();
  const isOneToMany = useIsOneToMany();
  const isSignAndSend = useIsSignAndSend();
  const [containerRef, { width: containerWidth, height: containerHeight }] = useElementSize();
  const { canvasWrapperRef, activeLabel } = useCanvasAccessibility();
  const { addAction, textAction } = useDatadog();
  const { getCanvasDimensionsWithBleed, getImageDimensionsWithBleed } = useDimensionsWithBleed();
  const { loadTextArea } = useTextArea();
  const { loadPhotoTextZone } = usePhotoTextZone();
  const { onPhotoSelect } = usePhotoControls();
  const handleIconAdded = useAddLowResIcon();
  const { fillPhotoZone, addPhotoToCardFace } = usePhoto();
  const runHistoryTransaction = useHistoryTransaction();
  const openImageEditDrawer = useOpenImageEditDrawer();
  const {
    IS_TEXTBOX_OVERLAY_ENABLED,
    IS_POD_TEXT_ZONE_OVERLAY_ENABLED,
    IS_FORCE_FILL_ENABLED,
    IS_SNS_TEXTBOX_OVERLAY_ENABLED,
    IS_SPLIT_INSIDE_PANEL_ENABLED,
    IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED,
    IS_PHOTO_TRAY_DRAG_TO_CANVAS_ENABLED,
  } = useFeatureFlags();

  // --- Derived state ---
  const currentCardFace = cardState.cardFacesList[`${cardState.activeCardIndex}`];
  const isInsideRight = currentCardFace?.type === 'inside-right';
  const { isUK, data } = initializedDataState;
  const projectTypeCode = initializedDataState.data?.project_type_code;
  const isMobileApp = appState.isMobileApp;
  const isLandscapeSas = projectTypeCode === CardType.SAS && cardState.cardFormat === 'landscape';
  const isPodTemplateTextOverlayEnabled = IS_TEXTBOX_OVERLAY_ENABLED && IS_POD_TEXT_ZONE_OVERLAY_ENABLED;
  const canvasDimensionsWithBleed = getCanvasDimensionsWithBleed(cardFace);
  const imageDimensionsWithBleed = getImageDimensionsWithBleed(cardFace);
  const canvasAriaLabel = useMemo(
    () => activeLabel || `${cardFace.type.replace('-', ' ')} card face`,
    [activeLabel, cardFace],
  );
  const faceReadiness = useCardFaceReadinessStore((state) =>
    state.faceReadiness.find(
      (face) =>
        face.editorSessionKey === editorSessionKey &&
        face.faceId === cardFace.faceId &&
        face.faceNumber === cardFace.faceNumber &&
        face.type === cardFace.type,
    ),
  );
  const isFaceReady = faceReadiness?.status === 'ready';
  const historyFaceIdentity = {
    editorSessionKey,
    faceId: cardFace.faceId,
    faceNumber: cardFace.faceNumber,
    type: cardFace.type,
  };

  // --- Local state ---
  const [hasEditingStarted, setHasEditingStarted] = useState(false);
  const [isResetTextModalOpen, setIsResetTextModalOpen] = useState(false);
  const [activeTextArea] = useState<fabric.Textbox | null>(null);
  const showInstructions = projectTypeCode === CardType.SAS && !hasEditingStarted;

  // --- Refs ---
  const instructionsRef = useRef<HTMLDivElement | null>(null);
  const editingHybridBox = useRef<boolean>(false);
  const checkSpellingRef = useRef<boolean>(false);
  const overlayTextAreaRef = useRef<OverlayTextHandles>(null);
  // PRCM-10916: tracks the last hovered photo zone id during a drag-over so the
  // highlight is only recomputed (and canvas re-rendered) when it changes,
  // since native `dragover` fires continuously as the pointer moves.
  const lastHoveredZoneId = useRef<string | null>(null);
  const loadGenerationRef = useRef(0);
  const myPhotozoneRef = useRef(new Map<string, fabric.Group>());
  const unregisterHistoryRestorerRef = useRef<(() => void) | null>(null);
  const historyRestorerRef = useRef<((snapshot: ReturnType<typeof captureCanvasSnapshot>) => Promise<void>) | null>(
    null,
  );
  const pendingTransformSnapshotRef = useRef<{
    target: fabric.Object;
    snapshot: ReturnType<typeof captureCanvasSnapshot>;
  } | null>(null);
  const pendingTextSnapshotRef = useRef<{
    target: fabric.Textbox;
    snapshot: ReturnType<typeof captureCanvasSnapshot>;
  } | null>(null);
  const pendingFaceSnapshotRef = useRef<ReturnType<typeof captureCanvasSnapshot> | null>(null);
  const pendingFaceDirtyRef = useRef(false);

  useEffect(() => {
    const targetCanvas = canvasRef.current;
    if (!historyCoordinator || !isFaceReady || !targetCanvas) {
      return;
    }
    pendingFaceSnapshotRef.current = captureCanvasSnapshot(targetCanvas);
    pendingFaceDirtyRef.current = false;
    const unregister = historyCoordinator.registerPendingTransaction(
      { editorSessionKey, faceId: cardFace.faceId, faceNumber: cardFace.faceNumber, type: cardFace.type },
      targetCanvas,
      {
        hasPendingChanges: () => pendingFaceDirtyRef.current,
        getBeforeSnapshot: () => pendingFaceSnapshotRef.current,
        isComposing: () => {
          const activeText = targetCanvas.getActiveObject() as
            | (fabric.Textbox & { inCompositionMode?: boolean })
            | undefined;
          return activeText?.inCompositionMode === true || overlayTextAreaRef.current?.isComposing?.() === true;
        },
        rebase: (snapshot) => {
          pendingFaceSnapshotRef.current = snapshot;
          pendingFaceDirtyRef.current = false;
          if (historyCoordinator.getState().isRestoring) {
            pendingTextSnapshotRef.current = null;
            pendingTransformSnapshotRef.current = null;
          } else {
            if (pendingTextSnapshotRef.current) pendingTextSnapshotRef.current.snapshot = snapshot;
            if (pendingTransformSnapshotRef.current) pendingTransformSnapshotRef.current.snapshot = snapshot;
          }
        },
      },
    );
    const updatePending = () => {
      if (!historyCoordinator.getState().isRestoring && pendingFaceSnapshotRef.current) {
        pendingFaceDirtyRef.current =
          JSON.stringify(captureCanvasSnapshot(targetCanvas)) !== JSON.stringify(pendingFaceSnapshotRef.current);
        historyCoordinator.refresh();
      }
    };
    targetCanvas.on('text:changed', updatePending);
    targetCanvas.on('history:composition', updatePending);
    return () => {
      targetCanvas.off('text:changed', updatePending);
      targetCanvas.off('history:composition', updatePending);
      unregister();
    };
  }, [historyCoordinator, isFaceReady, editorSessionKey, cardFace.faceId, cardFace.faceNumber, cardFace.type]);

  // PRCM-10916: mirror of the persistent click-selected photo zone id. The
  // canvas `drop`/`dragleave` handlers are registered once inside useFabric's
  // `onReady` closure, so reading `cardState.selectedPhotoZone` directly there
  // would capture a stale (mount-time) value. Read `.current` instead so the
  // click-selection highlight is restored against the live selection.
  const selectedPhotoZoneIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (historyCoordinator && historyRestorerRef.current) {
      unregisterHistoryRestorerRef.current?.();
      unregisterHistoryRestorerRef.current = historyCoordinator.registerFaceRestorer(
        { editorSessionKey, faceId: cardFace.faceId, faceNumber: cardFace.faceNumber, type: cardFace.type },
        historyRestorerRef.current,
      );
    }
    return () => {
      unregisterHistoryRestorerRef.current?.();
      unregisterHistoryRestorerRef.current = null;
    };
  }, [historyCoordinator, editorSessionKey, cardFace.faceId, cardFace.faceNumber, cardFace.type]);

  // PRCM-10916: the canvas `drop` handler is bound once inside useFabric's
  // `onReady` closure (at canvas init, BEFORE the card JSON — and thus the
  // photo zones — have loaded). Capturing the drop dependencies directly there
  // would freeze them at mount: most importantly `fillPhotoZone`, which closes
  // over `usePhoto`'s `canvasObjects` snapshot (empty at mount → it can never
  // find the target zone, so the dropped image is silently never added). Mirror
  // the live dependencies into a ref and read `.current` inside the handler.
  const dropDepsRef = useRef<Omit<Parameters<typeof onPhotoTrayImageDrop>[0], 'event' | 'canvas'>>();

  // --- Dependent hooks ---
  const { resetTextData, handleMouseUp } = useCanvasMouseUp({
    editingHybridBox,
    fabric,
    cardIndex,
    setIsResetTextModalOpen,
  });
  const canvasRef = useOverrideRef ? createRef<fabric.Canvas>() : cardFace.canvas;
  const { isTooltipOpen, tooltipPosition } = useLowResIconTooltip(canvasRef, !!isMobileApp);

  useEffect(() => {
    checkSpellingRef.current = appState.checkSpelling;
  }, [appState.checkSpelling]);

  useEffect(() => {
    selectedPhotoZoneIdRef.current = cardState.selectedPhotoZone?.id ?? null;
  }, [cardState.selectedPhotoZone?.id]);

  // PRCM-10916: keep the drop dependencies fresh on every render so the
  // once-bound canvas `drop` handler always uses the live `fillPhotoZone`
  // (and friends) rather than the mount-time closure. See `dropDepsRef` above.
  dropDepsRef.current = {
    data,
    isUK,
    setIsImageUploadDrawerOpen,
    hideMiddleControls,
    appDispatch,
    openImageEditDrawer,
    isReplacingImage,
    updateEditFormats,
    firstElementSelected,
    fillPhotoZone,
    handleIconAdded,
    addAction,
    // PRCM-10916: after a drop fills a zone, select that (now-filled) zone so
    // a subsequent tray-photo click replaces it instead of the first empty
    // zone. Overlay-safe: filled zones are skipped by the selection highlight.
    onZoneFilled: (photozoneId: string, image: fabric.Image) => setSelectedPhotoZone(cardDispatch, photozoneId, image),
    // PRCM-11096: Sign & Send cards have no photo capability, so drops are
    // ignored. Otherwise `cardFace.zones` decides zone-fill vs free-placement,
    // and `addPhotoToCardFace` free-places a drop on a zoneless face (parity
    // with the click free-add flow).
    isSignAndSend,
    currentCardFace: cardFace,
    addPhotoToCardFace,
    runHistoryTransaction,
  };

  // Event that fires when textbox text changes
  const onEditableTextChange = (textbox: fabric.Textbox) => {
    if (isEditableText(textbox)) {
      const { fixedWidth, maxFontSize, maxLines } = textbox.data;
      manageFabricTextSize({
        textbox,
        fixedWidth,
        maxLines,
        maxFontSize,
        onOverMaxLines: (fabricTextbox) => {
          // Throw max lines warning
          setIsToasterOpen(appDispatch, {
            variant: ToastVariants.Warning,
            children: t('editableText.toastWarningDescription'),
            title: t('editableText.toastWarningTitle'),
          });

          overlayTextAreaRef.current?.onMinTextLimitReached({ fabricTextbox });

          const canvas = activeCanvas?.current;
          if (canvas) {
            canvas?.setActiveObject(textbox);
          }
        },
      });
    }

    const canvas = activeCanvas?.current;
    if (canvas) {
      overlayTextAreaRef.current?.syncOverlayTextArea({
        canvas,
        fabricTextbox: textbox,
      });
    }
  };

  const onObjectSelection = useCallback(
    (selected: fabric.Object) => {
      // Hide resize controls when canResizeTextArea flag is set to false.
      if (selected.type === 'textbox') {
        textAction(ActionType.SELECT_TEXT, parseTextAction(selected as FabricTextBox));
        const textZone = selected as FabricTextBox;
        if (textZone.CanResizeTextArea === false) {
          textZone.setControlVisible('resizeControl', false);
          textZone.setControlsVisibility({
            mr: false,
            ml: false,
            bl: false,
            mtr: false,
            tr: false,
            tl: false,
            br: false,
          });
        } else {
          textZone.setControlVisible('resizeControl', true);
          textZone.setControlsVisibility({
            mr: true,
            ml: true,
            bl: true,
            mtr: true,
            tr: true,
            tl: true,
            br: true,
          });
        }

        if (textZone.CanDeleteTextArea === false) {
          textZone.setControlVisible('deleteControl', false);
          textZone.setControlsVisibility({
            mtr: false,
          });
        } else {
          textZone.setControlVisible('deleteControl', true);
          textZone.setControlsVisibility({
            mtr: true,
          });
        }
      }
      // Drawer should not be open when the user clicks on a text zone.
      if (selected?.data?.type === CanvasDataTypes.EditableText) {
        closeAllDrawers();
        return setAllDrawersClosed(appDispatch);
      }
      if (selected.type === 'textbox' && !(isSignAndSend && IS_SNS_TEXTBOX_OVERLAY_ENABLED)) {
        setAllDrawersClosed(appDispatch);
        openTextDrawer();
      }

      if (selected.name?.startsWith(CanvasDataTypes.PhotoTextZone)) {
        const photoTextZone = selected as fabric.Group;
        const originalTextbox = getGroupedItemByName(CanvasDataTypes.PhotoZoneTextbox, photoTextZone) as fabric.Textbox;
        if (
          originalTextbox &&
          originalTextbox.text &&
          originalTextbox.text.length > 0 &&
          !IS_SNS_TEXTBOX_OVERLAY_ENABLED
        ) {
          openTextDrawer();
        } else {
          closeAllDrawers();
          setAllDrawersClosed(appDispatch);
        }
      }

      if (selected.type === 'image') {
        onPhotoSelect();
      }
    },
    [appDispatch],
  );

  const handleResetTextOnClose = () => {
    closeResetTextModal(setIsResetTextModalOpen);
  };

  const handleResetTextConfirmation = () => {
    confirmResetText(resetTextData, setIsResetTextModalOpen);
  };

  const handleOnSpellcheckTrigger = useCallback(
    (options: fabric.IEvent<MouseEvent>) => {
      // Spellcheck is disabled - no action needed
    },
    [checkSpellingRef],
  );

  const canvas = useFabric({
    canvasRef,
    canvasParentRef: parentRef,
    canvasSettings: {
      preserveObjectStacking: true,
      enableRetinaScaling: true,
      selection: false,
    },
    onReady: (canvas) => {
      canvas.on('mouse:up', (options) => {
        handleMouseUp({
          options,
          canvasRef,
          canvas,
          overlayTextAreaRef,
        });
      });

      // Type any was added here because we were blocked by build errors when publishing.  We need
      // to go back and figure out the correct type, and why our linting isn't catching the error
      // when removing this type casting.
      canvas.on('selection:updated', (options: any) => {
        // Handle spell checking for cases when object selection is updated instead of a textbox being deselected
        handleOnSpellcheckTrigger(options);

        const selectedObj = options.selected && options.selected[0];
        if (selectedObj) {
          onObjectSelection(selectedObj);
        }
      });

      canvas.on('text:selection', (options) => {
        if (isPodTemplateTextOverlayEnabled) {
          return;
        }

        if (activeTextArea === null) {
          const textbox = options.target as fabric.Textbox;
          textbox.enterEditing();
        }
      });

      canvas.on('text:editing:entered', (options) => {
        const textbox = options.target as fabric.Textbox | undefined;
        if (historyCoordinator && textbox) {
          try {
            pendingTextSnapshotRef.current = {
              target: textbox,
              snapshot: pendingFaceSnapshotRef.current ?? captureCanvasSnapshot(canvas),
            };
          } catch {
            pendingTextSnapshotRef.current = null;
          }
        }
        if (IS_TEXTBOX_OVERLAY_ENABLED) {
          const fabricTextbox = options.target as fabric.Textbox;

          if (!fabricTextbox) return;
          if (fabricTextbox.data?.type === CanvasDataTypes.EditableText) {
            return;
          }
          try {
            const result = overlayTextAreaRef.current?.onShowOverlayTextarea({ fabricTextbox });
            // If overlay textarea couldn't be shown (returned undefined), fall back to default editing
            if (!result?.overlayTextarea || !result?.fabricTextbox) {
              // eslint-disable-next-line no-console
              console.warn('[CardFace] Overlay textarea unavailable, falling back to default editing');
              fabricTextbox.enterEditing();
            }
          } catch (error) {
            // Leaving console error in to help us debug issues with the overlay textarea if it appears.
            // eslint-disable-next-line no-console
            console.error('Error showing overlay textarea:', error);
            // Fallback to the default behavior if overlayTextArea is not available
            fabricTextbox.enterEditing();
          }
        }
      });

      canvas.on('text:editing:exited', (options) => {
        if (overlayTextAreaRef.current?.isOverlayTextareaVisible()) {
          return;
        }
        const pendingText = pendingTextSnapshotRef.current;
        const textbox = options.target as fabric.Textbox | undefined;
        if (!historyCoordinator || !pendingText || pendingText.target !== textbox) {
          return;
        }
        pendingTextSnapshotRef.current = null;
        void historyCoordinator
          .commitCapturedTransaction(historyFaceIdentity, canvas, pendingText.snapshot)
          .catch(() => undefined);
      });

      // Canvas event that triggers when text hanges
      canvas.on('text:changed', (event) => {
        const textbox = event.target as fabric.Textbox;
        if (!textbox) {
          return;
        }
        textAction(ActionType.EDIT_TEXT, {} as TextAction);
        onEditableTextChange(textbox);
      });

      canvas.on('before:transform', (event) => {
        if (!historyCoordinator) {
          return;
        }
        const transformEvent = event as { target?: fabric.Object; transform?: { target?: fabric.Object } };
        const target = transformEvent.transform?.target ?? transformEvent.target;
        if (!target || target.name === 'cropping-rect') {
          return;
        }
        try {
          pendingTransformSnapshotRef.current = {
            target,
            snapshot: captureCanvasSnapshot(canvas),
          };
        } catch {
          pendingTransformSnapshotRef.current = null;
        }
      });

      canvas.on('object:modified', (options) => {
        const pendingTransform = pendingTransformSnapshotRef.current;
        if (historyCoordinator && pendingTransform && pendingTransform.target === options.target) {
          pendingTransformSnapshotRef.current = null;
          void historyCoordinator
            .commitCapturedTransaction(historyFaceIdentity, canvas, pendingTransform.snapshot)
            .catch(() => undefined);
        }
        if (
          options?.action === 'rotate' &&
          options.target?.name !== 'cropping-rect' &&
          !IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED
        ) {
          setIsRotationDrawerOpen(appDispatch);
        }

        // PRCM-11096: track drag-reposition of a freely-placed (non-zone) card
        // face image so analytics can measure Inside-face repositioning. Only
        // free images are tagged `UserImage`; zone-filled images are retagged
        // `PhotoZoneImage` with a `photoZoneId` by `fillPhotoZone`, so the
        // explicit `!photoZoneId` guard keeps this from firing for zone drags.
        if (
          IS_PHOTO_TRAY_DRAG_TO_CANVAS_ENABLED &&
          options?.action === 'drag' &&
          options?.target?.data?.type === CanvasDataTypes.UserImage &&
          !options?.target?.data?.photoZoneId
        ) {
          addAction('reposition-photo-on-card-face', {
            cardFace: cardFace.type,
          });
        }
      });

      canvas.on('selection:cleared', (options) => {
        if (historyCoordinator?.getState().isRestoring) {
          return;
        }
        const selectionClearedTarget = options.e?.target;
        const isImageEditDrawerInteraction = isInsideImageEditToolbar(
          selectionClearedTarget instanceof Element ? selectionClearedTarget : null,
        );

        if (isImageEditDrawerInteraction) {
          return;
        }

        const isPhotoDrawerInteraction = isInsidePhotoDrawer(
          selectionClearedTarget instanceof Element ? selectionClearedTarget : null,
        );

        if (isPhotoDrawerInteraction) {
          return;
        }

        const isWamMenuInteraction = isInsideWamInstructionsMenu(
          selectionClearedTarget instanceof Element ? selectionClearedTarget : null,
        );

        if (isWamMenuInteraction) {
          return;
        }

        const deselectedElement = options.deselected?.[0];
        const isFloatingMenuInteraction = isFloatingToolMenuInteraction(
          selectionClearedTarget instanceof Element ? selectionClearedTarget : null,
        );

        if (isFloatingMenuInteraction) {
          if (deselectedElement?.name?.startsWith(CanvasDataTypes.PhotoTextZone)) {
            requestAnimationFrame(() => {
              canvas.setActiveObject(deselectedElement);
              canvas.renderAll();
            });
          }
          return;
        }

        const isOverlayVisible = overlayTextAreaRef.current?.isOverlayTextareaVisible();

        if (
          (deselectedElement && deselectedElement.data?.type === CanvasDataTypes.PhotoZoneImage) ||
          (deselectedElement && deselectedElement.data?.type === CanvasDataTypes.WamImage) ||
          (deselectedElement && deselectedElement.data?.type === CanvasDataTypes.UserImage)
        ) {
          addAction('deselect-image', {
            image: deselectedElement.data,
          });
        } else if (
          (deselectedElement && deselectedElement.data?.type === CanvasDataTypes.PhotoTextZone) ||
          (deselectedElement && deselectedElement.data?.type === CanvasDataTypes.EditableText)
        ) {
          textAction(ActionType.DESELECT_TEXT, parseTextAction(deselectedElement as FabricTextBox));
        }

        /* There doesn't appear to be a way to directly detect click events on the reset button object
         * using a boolean that activates when entering a hybrid TAM/WAM box
         */
        if (editingHybridBox.current) {
          trackWamTamReset(true);
          editingHybridBox.current = false;
        }

        // PRCM-10837: S&S deselect during edit. Canvas taps → transition to
        // select mode (reselect zone, keep drawer, skip overlay re-show).
        // Non-canvas deselects (drawer/toolbar focus shifts) → keep editing.
        const isCanvasTapDeselect =
          selectionClearedTarget instanceof Element && !!selectionClearedTarget.closest('.canvas-container, canvas');

        if (isOverlayVisible && isSignAndSend && deselectedElement?.name?.startsWith(CanvasDataTypes.PhotoTextZone)) {
          requestAnimationFrame(() => {
            canvas.setActiveObject(deselectedElement);
            if (!isCanvasTapDeselect) {
              const getTextZone = getGroupedItemByName(
                CanvasDataTypes.PhotoZoneTextbox,
                deselectedElement as fabric.Group,
              ) as fabric.Textbox;
              if (getTextZone) {
                overlayTextAreaRef.current?.onShowOverlayTextarea({ fabricTextbox: getTextZone });
              }
            }
            canvas.renderAll();
          });
          return; // Don't close drawers
        }

        if (!isOverlayVisible || !isSignAndSend) {
          setAllDrawersClosed(appDispatch);
          closeAllDrawers();
          // Also dismiss the toolbars when the active object
          // is deselected (e.g. clicking on empty canvas space).
          closeImageEditToolbar();
          closeFontToolbar();
        }
        handleOnSpellcheckTrigger(options);
      });

      if (IS_PHOTO_TRAY_DRAG_TO_CANVAS_ENABLED) {
        // PRCM-10916: routes a photo-tray drop into the specific grey photo
        // zone it landed on (parity with clicking a zone's + button); drops
        // that don't land on a zone are ignored inside `onPhotoTrayImageDrop`.
        canvas.on('drop', (event) => {
          // A successful drop consumes the drag interaction: clear the
          // drag-over highlight WITHOUT restoring the click-selection overlay
          // (`restoreSelection: false`) so no translucent/dashed overlay is
          // painted over the freshly dropped image. The dropped zone is then
          // marked selected AFTER it is filled (see `onZoneFilled` in
          // `dropDepsRef`), which is overlay-safe because a filled zone is
          // skipped by the selection highlight.
          clearPhotoZoneDragHighlight(canvas, lastHoveredZoneId, null, false);
          if (!dropDepsRef.current) {
            return;
          }
          onPhotoTrayImageDrop({
            event,
            canvas,
            ...dropDepsRef.current,
          });
        });

        // Required for the browser to allow a `drop` event to fire at all.
        canvas.on('dragenter', (event) => {
          event.e.preventDefault();
        });

        canvas.on('dragover', (event) => {
          handlePhotoZoneDragOver(canvas, event, lastHoveredZoneId);
        });

        canvas.on('dragleave', () => {
          clearPhotoZoneDragHighlight(canvas, lastHoveredZoneId, selectedPhotoZoneIdRef.current);
        });
      }

      canvas.setDimensions(canvasDimensionsWithBleed);
      const loadGeneration = loadGenerationRef.current + 1;
      loadGenerationRef.current = loadGeneration;
      const faceIdentity = {
        editorSessionKey,
        faceId: cardFace.faceId,
        faceNumber: cardFace.faceNumber,
        type: cardFace.type,
      };
      setFaceReadiness({ ...faceIdentity, status: 'loading', generation: loadGeneration });
      const bootstrapFace = async (presentation?: EditorPresentation, isHistoryRestore = false): Promise<void> => {
        const editableTextObjects = canvas
          .getObjects('textbox')
          .filter((object) => object.data?.type === CanvasDataTypes.EditableText);
        await Promise.all(
          editableTextObjects.map((object) =>
            loadTextArea({ canvas: canvasRef, editableText: object as fabric.Textbox, isHistoryRestore }),
          ),
        );
        await addEditableAreas(cardFace, cardState, false, isOneToMany, presentation);
        if (presentation) {
          await Promise.all(
            presentation
              .filter((helper) => helper.type === CanvasDataTypes.PhotoZoneButton)
              .map(async (helper) => {
                const objects = canvas.getObjects();
                const existing = objects.find(
                  (object) =>
                    object.data?.type === CanvasDataTypes.PhotoZoneButton &&
                    object.data?.photoZoneId === helper.photoZoneId,
                );
                if (existing) {
                  existing.set({ visible: helper.visible });
                  return;
                }
                const zone = objects.find(
                  (object) => object.data?.type === CanvasDataTypes.PhotoZone && object.name === helper.photoZoneId,
                );
                if (!zone) {
                  throw new Error('Historical camera button has no matching photo zone');
                }
                const button = await getPhotoZoneButton(zone, canvas, isUK);
                button.set({ visible: helper.visible });
                canvas.add(button);
              }),
          );
        }
        await addIconsToLowResImages(canvas, handleIconAdded);
      };
      myPhotozoneRef.current = new Map<string, fabric.Group>();
      const reviver: CanvasObjectReviver = (_jsonObject, fabricObject) => {
        const fabricItem = fabricObject as FabricObject;
        if (fabricItem.type === 'image') {
          hideMiddleControls(fabricItem);
        }

        loadPhotozoneObjects({
          fabricItem,
          myPhotozone: myPhotozoneRef.current,
          IS_FORCE_FILL_ENABLED,
          currentCardFace: cardFace,
          cardState,
        });

        if (fabricItem.name?.startsWith(CanvasDataTypes.PhotoTextZone) && fabricItem.data?.hasContent) {
          loadPhotoTextZone({ zone: fabricItem as fabric.Group });
        }

        if (fabricItem.isType('textbox')) {
          (fabricItem as fabric.Textbox).editable = appState.hasMouse;
          fabricItem.selectable = true;

          if (fabricItem.name?.startsWith('userTextbox')) {
            lockEditableSize(fabricItem as fabric.Textbox, cardState, activeCanvas, appDispatch, t);
          }

          if (fabricItem.data?.type === CanvasDataTypes.UserText) {
            const defaultTextMsg = `${t('cardEditor.userTextDefaultMessage')}`;
            const defaultColor = isUK ? '#333333' : initializedDataState.defaultColor || CUIColors.Black;
            addUserTextHandlers(fabricItem as fabric.Textbox, defaultTextMsg, defaultColor, canvas);
          }
        }

        if (fabricItem.isType('group')) {
          fabricItem.setControlsVisibility({
            mt: false,
            mb: false,
            ml: false,
            mr: false,
            bl: false,
            br: false,
            tl: false,
            tr: false,
            mtr: false,
          });
        }
      };

      unregisterHistoryRestorerRef.current?.();
      historyRestorerRef.current = async (snapshot) => {
        loadGenerationRef.current += 1;
        myPhotozoneRef.current = new Map<string, fabric.Group>();
        await enqueueCanvasOperation(canvas, async () => {
          await withPreservedCanvasFrame(canvas, async () => {
            await loadCanvasSnapshot(canvas, snapshot, reviver);
            await bootstrapFace(getHistoryEditorPresentation(snapshot), true);
          });
        });
      };
      unregisterHistoryRestorerRef.current =
        historyCoordinator?.registerFaceRestorer(faceIdentity, historyRestorerRef.current) ?? null;

      if (cardFace.originalCanvasJson) {
        const scaledCanvasJson = useCanvasScalingFactor(
          cardFace.originalCanvasJson,
          cardFace.type,
          cardState,
          projectTypeCode as string,
        );

        scaledCanvasJson.objects.forEach((obj: FabricObject) => {
          if (obj && obj.type === 'image') {
            (obj as fabric.Image).crossOrigin = 'anonymous';
            // If the image has been cropped, replace the src with the s3 cropped url.
            setCroppedSrc(obj as CustomFabricImage);
          }
          // Get dynamic_texboxes values from the template for photoTextZones
          if (obj && obj.name?.startsWith(CanvasDataTypes.PhotoTextZone)) {
            const textbox = obj as FabricTextBox;
            const templateTextField = findTextById(cardState.cardFacesList, textbox.data.ID);
            textbox.CanResizeTextArea = templateTextField?.CanResizeTextArea;
            textbox.CanEditFontSize = templateTextField?.CanEditFontSize;
          }
        });
        // the photozone is created first, then the img is attached
        void enqueueCanvasOperation(canvas, async () => {
          await loadCanvasSnapshot(canvas, scaledCanvasJson as unknown as Record<string, unknown>, reviver);
          await bootstrapFace();
          canvas.requestRenderAll();
          if (loadGenerationRef.current === loadGeneration) {
            setFaceReadiness({ ...faceIdentity, status: 'ready', generation: loadGeneration });
          }
        }).catch((error: unknown) => {
          if (loadGenerationRef.current === loadGeneration) {
            setFaceReadiness({ ...faceIdentity, status: 'fatal', generation: loadGeneration });
            setIsSystemErrorOpen(appDispatch, true);
          }
        });
      } else {
        const backgroundSrc = cardFace.backgroundImage.replaceAll('\\', '/');
        if (!backgroundSrc || backgroundSrc === '' || backgroundSrc.includes('?w=0')) {
          setFaceReadiness({ ...faceIdentity, status: 'fatal', generation: loadGeneration });
          setIsSystemErrorOpen(appDispatch, true);
          return;
        }
        void (async () => {
          await prepareFace?.(cardFace);
          await enqueueCanvasOperation(canvas, async () => {
            await new Promise<void>((resolve, reject) => {
              fabric.Image.fromURL(
                `${backgroundSrc}?w=${imageDimensionsWithBleed.width}`,
                (img: fabric.Image, isError?: boolean) => {
                  if (isError || !img) {
                    reject(new Error('Card face background image failed to load'));
                    return;
                  }
                  img.scaleToWidth(imageDimensionsWithBleed.width);
                  canvas.setBackgroundImage(img, resolve);
                },
                {
                  left: imageDimensionsWithBleed.left,
                  top: imageDimensionsWithBleed.top,
                  fill: 'transparent',
                  originX: 'left',
                  originY: 'top',
                  crossOrigin: 'anonymous',
                },
              );
            });
            await bootstrapFace();
            canvas.requestRenderAll();
            if (loadGenerationRef.current === loadGeneration) {
              setFaceReadiness({ ...faceIdentity, status: 'ready', generation: loadGeneration });
            }
          });
        })().catch(() => {
          if (loadGenerationRef.current === loadGeneration) {
            setFaceReadiness({ ...faceIdentity, status: 'fatal', generation: loadGeneration });
            setIsSystemErrorOpen(appDispatch, true);
          }
        });
      }

      if (cardState.cardFormat === 'landscape' && cardFace.type.startsWith('inside')) {
        const horizontalLine = new fabric.Rect({
          top: canvasDimensionsWithBleed.height / 2,
          width: cardFace.dimensions.width,
          height: 2,
          fill: 'gray',
          hasControls: false,
          evented: false,
          selectable: false,
          data: { type: CanvasDataTypes.FoldLine },
        });
        canvas.add(horizontalLine);
        canvas.renderAll();
      }
    },
  });

  const updateSize = useCallback(() => {
    const { width: canvasWidth, height: canvasHeight } = canvasRef.current || {};
    const areCanvasDimensionsSet =
      containerHeight && containerWidth && canvasHeight && canvasWidth && canvasRef.current;
    if (!areCanvasDimensionsSet) {
      return;
    }

    const availableContainerHeight = getAvailableCanvasHeight({
      containerElement: containerRef.current,
      containerHeight,
      instructionsElement: instructionsRef.current,
      isLandscapeSas,
      navigationElement: navigationRef?.current ?? null,
    });

    const scale = calculateAdjustedScale({
      containerWidth,
      canvasWidth,
      containerHeight: availableContainerHeight,
      canvasHeight,
      isMobile: isMobileApp,
      isOneToMany,
      projectTypeCode,
      isInsideRight,
      instructionsRef,
    });

    const zoom = canvasRef.current.getZoom() * scale;

    // Panel zoom for portrait inside faces (POD only — S&S uses inside-right natively)
    const isInsideFace = isSplitPanelFace(cardFace?.type);
    const isPortrait = cardState?.cardFormat === 'portrait';
    const isPanelZoom =
      IS_SPLIT_INSIDE_PANEL_ENABLED &&
      isInsideFace &&
      isPortrait &&
      (projectTypeCode === CardType.POD || projectTypeCode === CardType.DG) &&
      canvasWidth > 0;

    if (isPanelZoom) {
      setSplitPanelViewport({
        canvas: canvasRef.current,
        faceType: cardFace?.type,
        containerWidth,
        containerHeight,
        logicalWidth: canvasDimensionsWithBleed.width,
        logicalHeight: canvasDimensionsWithBleed.height,
      });
    } else {
      canvasRef.current.setDimensions({
        width: canvasWidth * scale,
        height: canvasHeight * scale,
      });
      canvasRef.current.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);
    }

    // Expose the rendered card width to the workspace panel so CSS can size
    // the toolbar host container. Only the active card face should set this —
    // inactive faces in the carousel have different widths and would overwrite
    // the correct value.
    if (cardIndex === cardState.activeCardIndex) {
      setCardRenderedWidth({
        containerRef,
        isPanelZoom,
        containerWidth,
        containerHeight,
        logicalWidth: canvasDimensionsWithBleed.width,
        logicalHeight: canvasDimensionsWithBleed.height,
        measuredWidth: canvasWrapperRef.current?.offsetWidth ?? 0,
      });
    }

    const panelZoom = Math.min(
      containerWidth / (canvasDimensionsWithBleed.width / 2),
      containerHeight / canvasDimensionsWithBleed.height,
    );
    const effectiveZoom = isPanelZoom ? panelZoom : zoom;

    const groups = canvasRef.current?.getObjects('group') as fabric.Group[];
    if (groups && groups.length > 0) {
      groups.forEach((group) => {
        const zoneDescription = getPhotoTextZoneDescription(group);
        if (zoneDescription && zoneDescription._objects?.length > 1) {
          const zoneWidth = zoneDescription.getScaledWidth();
          const zoomFactor = isMobileApp ? 1 - effectiveZoom / 2 : 1 / effectiveZoom;
          // get the offset for zone's label, necessary when a browser's (like FF) rendering results in larger char width
          const gap = 12 + (!isMobileApp ? getZoneLabelOffset(zoneDescription) : 0);
          // space content evenly after resize
          spaceContentEvenly(zoneDescription, gap * zoomFactor, -zoneWidth * zoomFactor);
        }
      });
    }
    canvasRef.current.renderAll();
  }, [
    activeCanvas,
    canvasRef.current,
    containerRef.current,
    containerHeight,
    containerWidth,
    cardFace?.type,
    cardState?.cardFormat,
    cardState?.activeCardIndex,
    isLandscapeSas,
    cardIndex,
  ]);

  const onEditingStarted = useCallback(() => setHasEditingStarted(true), []);

  useEffect(() => {
    if (showInstructions) {
      activeCanvas?.current?.on('mouse:down', onEditingStarted);
    }

    return () => {
      activeCanvas?.current?.off('mouse:down', onEditingStarted);
    };
  }, [activeCanvas, showInstructions]);

  useEffect(() => {
    if (activeCanvas?.current) {
      const photoTextZones = activeCanvas.current.getObjects().filter((obj) => obj.name?.startsWith('photoTextZone'));
      photoTextZones.forEach((zone) => {
        const warningIcon = getObjectByName(`${zone.name}-warning-icon`, activeCanvas.current as fabric.Canvas);
        if (warningIcon?.visible) {
          zone.set('borderColor', colors.error);
        }
      });
    }
  }, [activeCanvas]);

  useEffect(() => {
    updateSize();
  }, [activeCanvas, containerHeight, containerWidth]);

  useEffect(() => {
    const localCanvas = canvasRef.current;
    if (!localCanvas || cardIndex !== cardState.activeCardIndex) {
      return;
    }
    highlightSelectedPhotoZone(localCanvas, cardState.selectedPhotoZone?.id ?? null);
  }, [canvasRef, cardIndex, cardState.activeCardIndex, cardState.selectedPhotoZone?.id]);

  const handleObjectSelection = useCallback(
    (options) => {
      setHasEditingStarted(true);
      const selectedObj = options.selected && options.selected[0];
      const localCanvas = canvasRef.current;

      // Only this CardFace should handle events from its own canvas.
      if (!selectedObj || !localCanvas || selectedObj.canvas !== localCanvas) {
        return;
      }

      if (selectedObj && selectedObj.data) {
        const isEditableTextObject =
          !selectedObj.data.isFixed ||
          selectedObj.CanResizeTextArea ||
          selectedObj.CanDeleteTextArea ||
          selectedObj.data.displayFontToolbox;

        if (
          !isPodTemplateTextOverlayEnabled &&
          selectedObj.data.type === CanvasDataTypes.EditableText &&
          localCanvas &&
          !isEditableTextObject
        ) {
          selectedObj.enterEditing();
          selectedObj.selectAll();
          localCanvas.renderAll();
          if (selectedObj.hiddenTextarea) {
            selectedObj.hiddenTextarea.focus(); // focus on the hidden textarea
            if (isMobile) {
              ensureHiddenTextareaMinFont(selectedObj.hiddenTextarea);
            }
          }
        }
        onObjectSelection(selectedObj);
        // Track "start" only once per edit session.
        if (!firstElementSelected.current) {
          updateEditFormats({ eventAction: 'start' });
          const isPhotoTextZone = selectedObj.name?.indexOf(CanvasDataTypes.PhotoTextZone) !== -1;
          const isPhotoElement =
            isPhotoTextZone ||
            [CanvasDataTypes.PhotoZone, CanvasDataTypes.PhotoZoneImage, CanvasDataTypes.UserImage].includes(
              selectedObj.data?.type,
            );
          trackInitializeEdit(isPhotoElement ? 'photo' : 'text');
          firstElementSelected.current = true;
        }
      }
    },
    [isPodTemplateTextOverlayEnabled, onObjectSelection, updateEditFormats, trackInitializeEdit],
  );

  useEffect(() => {
    const localCanvas = canvasRef.current;
    localCanvas?.on('selection:created', handleObjectSelection);
    return () => {
      localCanvas?.off('selection:created', handleObjectSelection);
    };
  }, [canvasRef, handleObjectSelection]);

  const noInstructionsClassName = !showInstructions && !isLandscapeSas ? styles['card-face__no-instructions'] : '';

  return (
    <div
      className={`${classes} ${noInstructionsClassName} ${!isFaceReady ? styles['card-face--unready'] : ''}`}
      aria-busy={!isFaceReady}
    >
      <div className={styles['card-container']} ref={containerRef}>
        <div
          aria-label={canvasAriaLabel}
          aria-live="polite"
          className={styles['canvas-wrapper']}
          ref={canvasWrapperRef}
          tabIndex={-1}
          id={`${cardFace.faceNumber}_TABPANEL`}
          role="tabpanel"
          aria-labelledby={`${cardFace.faceNumber}_TAB`}
          data-card-face-number={cardFace.faceNumber}
          data-testid={`canvas-wrapper-${cardFace.faceNumber}`}
        >
          {IS_TEXTBOX_OVERLAY_ENABLED && (
            <OverlayTextarea canvasRef={canvasRef} cardFace={cardFace} ref={overlayTextAreaRef} />
          )}
          <FabricCanvas canvasRef={canvas} />
          <LowResolutionTooltipTrigger isOpen={isTooltipOpen} position={tooltipPosition} />
          {projectTypeCode === CardType.SAS && cardState?.activeCardIndex > 0 && (
            <div
              className={`${
                cardState.cardFormat === 'landscape' ? styles['landscape-instructions'] : styles['instructions']
              } ${hasEditingStarted ? styles['instructions__hidden'] : ''}`}
              ref={instructionsRef}
            >
              <CUIText variant="h5" color="textPrimary" className={styles['instructions-text']}>
                {t('cardFace.personalMessageInside')}
              </CUIText>
            </div>
          )}
          {projectTypeCode === CardType.SAS && cardState?.activeCardIndex === 0 && (
            <div
              className={`${
                cardState.cardFormat === 'landscape' ? styles['landscape-instructions'] : styles['instructions']
              } ${hasEditingStarted ? styles['instructions__hidden'] : ''}`}
              ref={instructionsRef}
            >
              <CUIText variant="h5" color="textPrimary" className={styles['instructions-text']}>
                {t('cardFace.personalMessageOutside')}
              </CUIText>
            </div>
          )}
        </div>
        <ConfirmationDialog
          id="reset-text-confirmation-dialog"
          testId="reset-text-confirmation-dialog"
          isOpen={isResetTextModalOpen}
          onCancel={handleResetTextOnClose}
          onConfirm={handleResetTextConfirmation}
          title={t('resetTextDialogPhotoTextZone.title') as string}
          confirmLabel={t('resetTextDialogPhotoTextZone.actionButton') as string}
          cancelLabel={t('confirmationDialog.cancelButton') as string}
          accentIcon={IconNames.CardFeaturesBold}
        >
          {t('resetTextDialogPhotoTextZone.content') as string}
        </ConfirmationDialog>
      </div>
    </div>
  );
};
